/**
 * The ONLY module that talks to the Clutch server. Everything the UI needs goes
 * through `ClutchClient`, so a change in server framing is a one-file change here.
 *
 * Assumptions about the server (SPEC §5, Hono on :8787, proxied by Vite at /api):
 *  - JSON bodies in and out. Errors are non-2xx; a lock rejection may come back
 *    either as 2xx or 4xx but always carries a `CallLock` body with status 'rejected'.
 *  - List endpoints may return a bare array or an object wrapping it
 *    (`{games: [...]}` / `{moments: [...]}`); both are accepted.
 *  - Errors are non-2xx with `{error: string}`. Lock: 200 confirmed / 409 rejected,
 *    both with a `CallLock` body. Receipt: 404 (no lock) / 409 `not_resolved`.
 *  - `POST /rooms` → 201 RoomSnapshot (replay already playing).
 *    `POST /rooms/:id/join` → `{roomId, fanId, snapshot}` (body ignored here).
 *  - `GET /rooms/:id/moments` → MomentTrace (below).
 *  - SSE frames are NAMED: `event: <kind>`, `id: <seq>`, `data: <JSON>`. Room stream
 *    data is a full `RoomEvent` ({roomId, seq, at, kind, data}); the first frame is
 *    always `snapshot` (also after every reconnect). `?fanId=` marks the fan online.
 *    Fan stream: `event: invite|invite_update`, data = `FanEvent`; backlog replays.
 *    Unnamed (`message`) frames are accepted too, for robustness.
 *  - `GET /rooms/:id/plays` → GameEvent[] applied so far (seq ≤ cursor, no lookahead).
 *  - `GET /rooms/:id/rewind?fanId=` → Rewind. Both are optional: 404 → empty / none.
 */
import type {
  CallLock,
  CallOffer,
  DirectorProfile,
  Fan,
  FanEvent,
  GameEvent,
  GameMedia,
  GameMeta,
  LiveGame,
  LiveGameContext,
  LockRequest,
  MomentDecision,
  ReactionEmoji,
  Receipt,
  Rewind,
  RoomEvent,
  RoomSnapshot,
  Take,
} from '@clutch/core';
import { GameMediaSchema } from '@clutch/core';
import { createDirectorApi, type DirectorApi } from './director/api';

/** What the client sends with a take's media (validated server-side by `TakeMetaSchema`). */
export type TakeMeta = {
  fanId: string;
  mimeType: string;
  durationMs: number;
  startedAtSeq: number;
  endedAtSeq: number;
  marks: { seq: number; offsetMs: number }[];
};

export type FanCreate = {
  displayName: string;
  team?: string;
  player?: string;
  budgetPerWeek: 1 | 3 | 7;
};

export type LibraryTake = { take: Take; game: GameMeta };
export type ModerationReport = { reportId: string; roomId: string; takeId: string; reason: string; at: string; take?: Take };

export type ControlCommand =
  | { action: 'play' }
  | { action: 'pause' }
  | { action: 'speed'; speed: number }
  | { action: 'seek'; seq: number };

export type StreamStatus = 'connecting' | 'open' | 'reconnecting';

export type MomentTrace = {
  modelVersion: string;
  detectorVersion: string;
  observed: number;
  fired: number;
  moments: MomentDecision[]; // every decision that fired
  nearMisses: MomentDecision[]; // highest-scoring decisions that were held
  recent: MomentDecision[]; // last evaluated decisions, oldest first
};

export const ROOM_EVENT_KINDS = [
  'snapshot',
  'game_event',
  'probability',
  'moment',
  'call_offer',
  'call_lock',
  'call_closed',
  'presence',
  'reaction',
  'resolution',
  'replay',
  'take',
  'take_removed',
  'feed',
] as const satisfies readonly RoomEvent['kind'][];
export const FAN_EVENT_KINDS = ['invite', 'invite_update'] as const satisfies readonly FanEvent['kind'][];

export interface ClutchClient {
  readonly mode: 'server' | 'fixture';
  session(): Promise<{ required: boolean; fan?: Fan }>;
  createFan(input: FanCreate): Promise<Fan>;
  updateFan(fanId: string, input: FanCreate & { directorProfile?: Partial<DirectorProfile> }): Promise<Fan>;
  subscribeFan(fanId: string, onEvent: (e: FanEvent) => void, onStatus?: (s: StreamStatus) => void): () => void;
  listGames(): Promise<GameMeta[]>;
  /** Replay rooms take a start seq + speed; live rooms (`espn:<id>`) take only the game id. */
  createRoom(input: { gameId: string; startAtSeq?: number; speed?: number; community?: boolean; mode?: 'live' | 'replay' | 'recap' }): Promise<{ roomId: string }>;
  /** Current Eastern schedule day and the previous six days. Throws when discovery fails. */
  liveGames(): Promise<LiveGame[]>;
  scheduleCoverage?(): { missingDates: readonly string[] };
  /** Optional, observed context from existing public rooms. Does not start a live feed. */
  liveContext?(): Promise<LiveGameContext[]>;
  /** Official embeddable video for a game. undefined on 404 / unavailable (hide the UI). */
  media(gameId: string): Promise<GameMedia | undefined>;
  /** A fan challenges the room: opens a call when none exists. */
  openCall(roomId: string, fanId: string): Promise<CallOffer>;
  /** Publish a take (multipart: `meta` JSON + `media` file). */
  postTake(roomId: string, meta: TakeMeta, media: Blob): Promise<Take>;
  /** Takes published in a room. [] when unavailable. */
  takes(roomId: string): Promise<Take[]>;
  fanTakes(fanId: string): Promise<LibraryTake[]>;
  deleteTake(take: Take): Promise<void>;
  reportTake(take: Take, reason: string): Promise<void>;
  moderationReports(token: string): Promise<ModerationReport[]>;
  moderateTake(take: Take, token: string): Promise<void>;
  /** A take's playable URL (server: the API path, proxied; fixture: a blob URL). */
  takeUrl(take: Take): string;
  getRoom(roomId: string): Promise<RoomSnapshot>;
  joinRoom(roomId: string, fanId: string): Promise<void>;
  lock(roomId: string, callId: string, body: LockRequest): Promise<CallLock>;
  react(roomId: string, fanId: string, emoji: ReactionEmoji): Promise<void>;
  /** `fanId` marks that fan online in the room's presence while the stream is open. */
  subscribeRoom(
    roomId: string,
    onEvent: (e: RoomEvent) => void,
    onStatus?: (s: StreamStatus) => void,
    fanId?: string,
  ): () => void;
  getReceipt(roomId: string, fanId: string): Promise<Receipt>;
  control(roomId: string, cmd: ControlCommand): Promise<void>;
  moments(roomId: string): Promise<MomentTrace>;
  /** Plays the room has applied so far (for the court stage). [] when unavailable. */
  plays(roomId: string): Promise<GameEvent[]>;
  /** "What you missed" for a fan joining mid-game. undefined when unavailable. */
  rewind(roomId: string, fanId: string): Promise<Rewind | undefined>;
  /** Personal director (server only; the dev fixture driver has none → director UI hides). */
  director?: DirectorApi;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

type FetchLike = typeof fetch;

/** Accept `[...]` or `{ <key>: [...] }`. */
export function unwrapArray<T>(body: unknown, key: string): T[] {
  if (Array.isArray(body)) return body as T[];
  if (body && typeof body === 'object' && Array.isArray((body as Record<string, unknown>)[key])) {
    return (body as Record<string, T[]>)[key]!;
  }
  throw new ApiError(200, `expected an array or {${key}: []}`, body);
}

/** Is this parsed SSE payload shaped like a RoomEvent? (light guard, server is trusted) */
export function isRoomEvent(x: unknown): x is RoomEvent {
  return !!x && typeof x === 'object' && typeof (x as RoomEvent).kind === 'string' && 'data' in x;
}
export function isFanEvent(x: unknown): x is FanEvent {
  const k = (x as FanEvent | null)?.kind;
  return k === 'invite' || k === 'invite_update';
}

function isCallLock(x: unknown): x is CallLock {
  const s = (x as CallLock | null)?.status;
  return s === 'confirmed' || s === 'rejected';
}

/** Server take URLs are API paths (`/rooms/:id/takes/:takeId/media`); route them through the app's API base. */
export function takeMediaUrl(base: string, take: Pick<Take, 'url' | 'roomId' | 'takeId'>): string {
  const u = take.url;
  if (/^(https?:|blob:|data:)/.test(u)) return u;
  if (u.startsWith(`${base}/`)) return u;
  if (u.startsWith('/')) return `${base}${u}`;
  return `${base}/rooms/${encodeURIComponent(take.roomId)}/takes/${encodeURIComponent(take.takeId)}/media`;
}

export function createHttpClient(
  base = '/api',
  deps: { fetch?: FetchLike; EventSource?: typeof EventSource } = {},
): ClutchClient {
  const f: FetchLike = deps.fetch ?? ((...a) => fetch(...a));
  const ES = deps.EventSource ?? (typeof EventSource !== 'undefined' ? EventSource : undefined);

  async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const form = typeof FormData !== 'undefined' && body instanceof FormData;
    const res = await f(`${base}${path}`, {
      method,
      headers: body === undefined || form ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : form ? (body as FormData) : JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown = undefined;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = text;
    }
    if (!res.ok) {
      // A rejected lock is a valid answer, not a transport failure.
      if (isCallLock(parsed)) return parsed as T;
      const msg = (parsed as { error?: unknown } | undefined)?.error;
      throw new ApiError(res.status, typeof msg === 'string' ? msg : `${method} ${path} → ${res.status}`, parsed);
    }
    return parsed as T;
  }

  function stream<T>(
    path: string,
    kinds: readonly string[],
    guard: (x: unknown) => x is T,
    onEvent: (e: T) => void,
    onStatus?: (s: StreamStatus) => void,
  ): () => void {
    if (!ES) throw new Error('EventSource unavailable');
    onStatus?.('connecting');
    const es = new ES(`${base}${path}`);
    es.onopen = () => onStatus?.('open');
    es.onerror = () => onStatus?.('reconnecting'); // EventSource retries with Last-Event-ID
    const handle = (m: MessageEvent<string>) => {
      try {
        const parsed: unknown = JSON.parse(m.data);
        if (guard(parsed)) onEvent(parsed);
      } catch {
        /* ignore malformed frames */
      }
    };
    for (const k of kinds) es.addEventListener(k, handle as EventListener);
    es.onmessage = handle;
    return () => es.close();
  }

  const enc = encodeURIComponent;
  let missingDates: string[] = [];
  return {
    mode: 'server',
    director: createDirectorApi(base, f),
    session: () => req('GET', '/session'),
    createFan: (input) => req<Fan>('POST', '/fans', input),
    updateFan: (fanId, input) => req<Fan>('PATCH', `/fans/${enc(fanId)}`, input),
    subscribeFan: (fanId, onEvent, onStatus) => stream(`/fans/${enc(fanId)}/stream`, FAN_EVENT_KINDS, isFanEvent, onEvent, onStatus),
    listGames: async () => unwrapArray<GameMeta>(await req('GET', '/games'), 'games'),
    createRoom: async (input) => {
      const r = await req<{ roomId: string }>('POST', '/rooms', input);
      if (!r || typeof r.roomId !== 'string') throw new ApiError(200, 'createRoom: missing roomId', r);
      return { roomId: r.roomId };
    },
    liveGames: async () => {
      // Shared server cache owns the seven Eastern schedule days, not each fan/browser.
      const result = await req<unknown>('GET', '/live/games?days=7');
      missingDates = result && typeof result === 'object' && 'missingDates' in result && Array.isArray(result.missingDates) ? result.missingDates.filter((d): d is string => typeof d === 'string') : [];
      return unwrapArray<LiveGame>(result, 'games');
    },
    scheduleCoverage: () => ({ missingDates }),
    liveContext: async () => {
      try { return unwrapArray<LiveGameContext>(await req('GET', '/live/context'), 'contexts'); }
      catch { return []; }
    },
    media: async (gameId) => {
      try {
        const parsed = GameMediaSchema.safeParse(await req('GET', `/games/${enc(gameId)}/media`));
        return parsed.success ? parsed.data : undefined;
      } catch {
        return undefined; // 404 → no official video for this game: hide it
      }
    },
    openCall: (roomId, fanId) => req<CallOffer>('POST', `/rooms/${enc(roomId)}/calls`, { fanId }),
    postTake: async (roomId, meta, media) => {
      const fd = new FormData();
      fd.append('meta', JSON.stringify(meta));
      const ext = meta.mimeType.includes('mp4') ? 'mp4' : 'webm';
      fd.append('media', media, `take.${ext}`);
      return req<Take>('POST', `/rooms/${enc(roomId)}/takes`, fd);
    },
    takes: async (roomId) => {
      try {
        return unwrapArray<Take>(await req('GET', `/rooms/${enc(roomId)}/takes`), 'takes');
      } catch {
        return [];
      }
    },
    takeUrl: (take) => takeMediaUrl(base, take),
    fanTakes: async (fanId) => unwrapArray<LibraryTake>(await req('GET', `/fans/${enc(fanId)}/takes`), 'takes'),
    deleteTake: async (take) => {
      const response = await f(`${base}/rooms/${enc(take.roomId)}/takes/${enc(take.takeId)}`, { method: 'DELETE', headers: { 'x-clutch-fan': take.fanId } });
      if (!response.ok) throw new ApiError(response.status, 'Couldn’t delete the take.');
    },
    reportTake: async (take, reason) => { await req('POST', `/rooms/${enc(take.roomId)}/takes/${enc(take.takeId)}/report`, { reason }); },
    moderationReports: async (token) => {
      const response = await f(`${base}/moderation/reports`, { headers: { 'x-clutch-admin': token } });
      if (!response.ok) throw new ApiError(response.status, 'Moderator access required.');
      return response.json() as Promise<ModerationReport[]>;
    },
    moderateTake: async (take, token) => {
      const response = await f(`${base}/rooms/${enc(take.roomId)}/takes/${enc(take.takeId)}`, { method: 'DELETE', headers: { 'x-clutch-admin': token } });
      if (!response.ok) throw new ApiError(response.status, 'Couldn’t remove the take.');
    },
    getRoom: (roomId) => req<RoomSnapshot>('GET', `/rooms/${enc(roomId)}`),
    joinRoom: async (roomId, fanId) => {
      await req('POST', `/rooms/${enc(roomId)}/join`, { fanId });
    },
    lock: async (roomId, callId, body) => {
      const r = await req<unknown>('POST', `/rooms/${enc(roomId)}/calls/${enc(callId)}/lock`, body);
      if (!isCallLock(r)) throw new ApiError(200, 'lock: unexpected body', r);
      return r;
    },
    react: async (roomId, fanId, emoji) => {
      await req('POST', `/rooms/${enc(roomId)}/reactions`, { fanId, emoji });
    },
    subscribeRoom: (roomId, onEvent, onStatus, fanId) =>
      stream(
        `/rooms/${enc(roomId)}/stream${fanId ? `?fanId=${enc(fanId)}` : ''}`,
        ROOM_EVENT_KINDS,
        isRoomEvent,
        onEvent,
        onStatus,
      ),
    getReceipt: (roomId, fanId) => req<Receipt>('GET', `/rooms/${enc(roomId)}/receipts/${enc(fanId)}`),
    control: async (roomId, cmd) => {
      await req('POST', `/rooms/${enc(roomId)}/control`, cmd);
    },
    plays: async (roomId) => {
      try {
        const body = await req<unknown>('GET', `/rooms/${enc(roomId)}/plays`);
        return unwrapArray<GameEvent>(body, 'plays');
      } catch {
        return []; // endpoint missing or room gone: the court fills from live plays
      }
    },
    rewind: async (roomId, fanId) => {
      try {
        const r = await req<Rewind>('GET', `/rooms/${enc(roomId)}/rewind?fanId=${enc(fanId)}`);
        return r && Array.isArray(r.beats) ? r : undefined;
      } catch {
        return undefined; // no rewind endpoint → no rewind, never a made-up one
      }
    },
    moments: async (roomId) => {
      const body = await req<unknown>('GET', `/rooms/${enc(roomId)}/moments`);
      if (Array.isArray(body)) {
        const all = body as MomentDecision[];
        const fired = all.filter((m) => m.isMoment);
        return { modelVersion: '', detectorVersion: all[0]?.detectorVersion ?? '', observed: all.length, fired: fired.length, moments: fired, nearMisses: [], recent: all.slice(-20) };
      }
      return body as MomentTrace;
    },
  };
}
